/**
 * API client pour les rapports de clôture (Rapport Z)
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Récupère le tenant ID depuis localStorage
 */
function getTenantId(): string | null {
  try {
    const tenantData = localStorage.getItem('tenant');
    if (tenantData) {
      const tenant = JSON.parse(tenantData);
      return tenant?.id || null;
    }
  } catch (e) {
    console.error('Failed to parse tenant data:', e);
  }
  return null;
}

/**
 * Récupère le token d'authentification depuis localStorage
 */
function getAuthToken(): string | null {
  const customerToken = localStorage.getItem('customer_token');
  const staffToken = localStorage.getItem('auth_token');
  return customerToken || staffToken;
}

/**
 * Crée les headers avec X-Tenant-ID et Authorization
 */
function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const tenantId = getTenantId();
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  return headers;
}

/**
 * Structure d'un rapport de clôture
 */
export interface ClosingReport {
  id: string;
  tenant_id: string;
  report_number: string;
  sequence: number;
  report_date: string;
  period_start: string;
  period_end: string;

  // Totaux ventes
  total_sales_count: number;
  total_sales_cents: number;
  total_sales_ht_cents: number;
  total_tax_cents: number;

  // Ventilation TVA (Belgique: 0%, 6%, 21%)
  vat_0_base_cents: number;
  vat_6_base_cents: number;
  vat_6_amount_cents: number;
  vat_21_base_cents: number;
  vat_21_amount_cents: number;

  // Modes de paiement
  payment_cash_cents: number;
  payment_card_cents: number;
  payment_wallet_cents: number;
  payment_other_cents: number;

  // Annulations et remboursements
  cancellations_count: number;
  cancellations_cents: number;
  refunds_count: number;
  refunds_cents: number;

  // Sorties de stock
  stock_out_count: number;
  stock_out_cost_cents: number;
  stock_out_sale_cents: number;

  // Prélèvements personnels
  personal_use_count: number;
  personal_use_cents: number;

  // Remises
  discount_total_cents: number;
  discount_count: number;

  // Fond de caisse
  opening_cash_cents?: number;
  closing_cash_cents?: number;
  expected_cash_cents?: number;
  cash_difference_cents?: number;

  // Panier moyen
  average_basket_cents: number;

  // Opérateur
  closed_by?: string;
  closed_by_name?: string;
  closed_at: string;

  // Intégrité
  signature?: string;
  pdf_url?: string;
  pdf_hash?: string;

  notes?: string;
  metadata?: Record<string, any>;

  created_at: string;
  updated_at: string;
}

/**
 * Structure pour la prévisualisation du rapport
 */
export interface ClosingReportPreview {
  report_date: string;
  period_start: string;
  period_end: string;

  // Totaux ventes (CA magasin, hors factures)
  total_sales_count: number;
  invoiced_sales_count: number; // ventes POS transformées en facture (exclues du CA magasin)
  total_sales_cents: number;
  total_sales_ht_cents: number;
  total_tax_cents: number;

  // Ventilation TVA (Belgique: 0%, 6%, 21%)
  vat_breakdown: {
    rate_0?: { base_cents: number };
    rate_6: { base_cents: number; amount_cents: number };
    rate_21: { base_cents: number; amount_cents: number };
  };

  // Modes de paiement
  payment_methods: {
    cash_cents: number;
    bancontact_cents: number;
    meal_voucher_cents: number;
    wero_cents: number;
    wallet_cents: number;
    online_cents: number;
    virement_cents: number;
    virement_invoiced_cents?: number;
    virement_non_invoiced_cents?: number;
    other_cents: number;
    cash_invoiced_cents?: number;
    bancontact_invoiced_cents?: number;
    invoiced_total_cents?: number;
  };

  // Retraits e-commerce
  ecom_pickup?: {
    count: number;
    total_cents: number;
  };

  // Annulations et remboursements
  cancellations: {
    count: number;
    total_cents: number;
  };
  refunds: {
    count: number;
    total_cents: number;
  };

  // Remboursements POS
  pos_refunds?: {
    count: number;
    total_cents: number;
  };

  // Activité wallet
  wallet_activity?: {
    deposits_count: number;
    deposits_cents: number;
    purchases_count: number;
    purchases_cents: number;
    refunds_count: number;
    refunds_cents: number;
  };

  /** Bons cadeaux : de la DETTE, jamais du chiffre d'affaires. `issued_*` = vendus
   *  aujourd'hui (encaissés, donc dans les moyens de paiement, mais hors CA) ;
   *  `redeemed_*` = convertis en FOLIES (la dette change de forme, pas de montant) ;
   *  `outstanding_cents` = encours restant dû aux porteurs à la clôture. Optionnel :
   *  une API antérieure à ce lot ne l'envoie pas. */
  gift_vouchers?: {
    issued_count: number;
    issued_cents: number;
    redeemed_count: number;
    redeemed_cents: number;
    outstanding_cents: number;
  };

  // Sorties de stock
  stock_out: {
    count: number;
    cost_cents: number;
    sale_cents: number;
  };

  // Prélèvements personnels (TVA sur prix d'achat, hors CA)
  personal_use: {
    count: number;
    total_cents: number;
    ht_cents?: number;
    vat_cents?: number;
  };

  // Panier moyen
  average_basket_cents: number;

  // Ventilation par vendeur
  vendor_breakdown?: Array<{
    vendor_id: string | null;
    vendor_name: string | null;
    sales_count: number;
    sales_cents: number;
  }>;

  // Factures Enfact (POS)
  enfact_invoices?: {
    count: number;
    total_ht_cents: number;
    total_ttc_cents: number;
    invoices: Array<{
      id: string;
      enfact_invoice_number: string;
      customer_name: string | null;
      total_ht_cents: number;
      total_ttc_cents: number;
      status: string;
    }>;
  };

  // Consignes
  deposits?: {
    collected_count: number;
    collected_cents: number;
    returned_count: number;
    returned_cents: number;
    net_cents: number;
  };

  // Rendu monnaie total
  total_change_cents: number;

  // Remises
  discount_total_cents: number;
  discount_count: number;

  // Métadonnées
  existing_report?: ClosingReport;
}

export interface FamilyAgg {
  count: number;
  cost_cents: number;
  sale_cents: number;
  vat_cents: number;
}

export interface ChannelAgg {
  count?: number;
  ttc_cents: number;
  ht_cents: number;
  vat_cents: number;
}

export interface ClosingReportSummary {
  from: string;
  to: string;
  reports_count: number;
  sales: { count: number; ttc_cents: number; ht_cents: number; tax_cents: number };
  channels: {
    retail: ChannelAgg;
    b2b: ChannelAgg;
    ecom: ChannelAgg;
    total: { ttc_cents: number; ht_cents: number; vat_cents: number };
  };
  vat: { base_0_cents: number; base_6_cents: number; amount_6_cents: number; base_21_cents: number; amount_21_cents: number };
  payments: {
    cash_cents: number;
    bancontact_cents: number;
    meal_voucher_cents: number;
    wero_cents: number;
    wallet_cents: number;
    online_cents: number;
    virement_cents: number;
    card_cents: number;
    other_cents: number;
  };
  cash_difference_cents: number;
  discounts: { count: number; total_cents: number };
  cancellations: { count: number; total_cents: number };
  refunds: { count: number; total_cents: number };
  ecom_pickup: { count: number; total_cents: number };
  stock_outs: {
    by_family: { prelevements: FamilyAgg; pertes: FamilyAgg; dons: FamilyAgg; retours: FamilyAgg };
    prelevements_by_person: Array<{ personne: string; count: number; cost_cents: number; sale_cents: number; vat_cents: number }>;
    without_cost_count: number;
  };
}

export const closingReportApi = {
  /**
   * Synthèse comptable des rapports Z sur une période
   */
  async getSummary(from: string, to: string): Promise<ClosingReportSummary> {
    const params = new URLSearchParams({ from, to });
    const response = await fetch(`${API_BASE_URL}/closing-reports/summary?${params}`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Erreur lors de la synthèse');
    }
    return response.json();
  },

  /**
   * Synchroniser manuellement les factures Enfact d'une période (import headers, sans stock)
   */
  async syncEnfact(from: string, to: string): Promise<{ imported: number; skipped: number }> {
    const tenantId = getTenantId();
    const response = await fetch(`${API_BASE_URL}/enfact/${tenantId}/import/sync`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ from_date: from, to_date: to }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Synchronisation Enfact échouée');
    }
    return response.json();
  },

  /**
   * Prévisualiser les données du rapport sans le créer
   */
  async preview(date?: string): Promise<ClosingReportPreview> {
    const params = date ? `?date=${date}` : '';
    const response = await fetch(`${API_BASE_URL}/closing-reports/preview${params}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la prévisualisation');
    }

    return response.json();
  },

  /**
   * Créer un rapport de clôture (génère le PDF et l'archive sur S3)
   */
  async create(dto: {
    date?: string;
    notes?: string;
    closed_by?: string;
    closed_by_name?: string;
    opening_cash_cents?: number;
    closing_cash_cents?: number;
  }): Promise<ClosingReport> {
    const response = await fetch(`${API_BASE_URL}/closing-reports`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la création du rapport');
    }

    return response.json();
  },

  /**
   * Obtenir le rapport du jour s'il existe
   */
  async getTodayReport(): Promise<{ exists: boolean; report?: ClosingReport; message?: string }> {
    const response = await fetch(`${API_BASE_URL}/closing-reports/today`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la récupération');
    }

    return response.json();
  },

  /**
   * Lister les rapports de clôture
   */
  async list(limit = 30, offset = 0): Promise<ClosingReport[]> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    const response = await fetch(`${API_BASE_URL}/closing-reports?${params}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des rapports');
    }

    return response.json();
  },

  /**
   * Obtenir un rapport par ID
   */
  async get(id: string): Promise<ClosingReport> {
    const response = await fetch(`${API_BASE_URL}/closing-reports/${id}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération du rapport');
    }

    return response.json();
  },
};
